import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilTrash } from "@coreui/icons";
import { useCartStore, type UpgradeLine } from "../../state/cartStore";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { useMySubscriptions } from "../../api/hooks/useStrategySubscriptions";
import { offersForLine, usableOffers, useUsableOffers } from "../../api/hooks/useOffers";
import { routePaths } from "../../routes/route-paths";
import { formatPriceUsd } from "../../lib/money";
import { aYearOn, formatDateShort, formatDayMonth } from "../../lib/dates";
import { savingOnLine } from "../../lib/offer-savings";
import { StrategyIdentityBand } from "./components/StrategyIdentityBand";
import { StrategyMetaPills } from "./components/StrategyMetaPills";
import { StrategySideLabel } from "./components/StrategySideLabel";
import { UpgradeLineOffer } from "./components/UpgradeLineOffer";

/**
 * The cart while it holds an upgrade. Nothing is due today and the year starts on the next bill, so
 * the line shows its dates and what is taken on each, in place of the purchase cart's order total.
 */
export function UpgradeCart({ line }: { line: UpgradeLine }) {
  const navigate = useNavigate();
  const { data: strategies } = useTradingStrategies();
  const subscriptions = useMySubscriptions();
  const offers = usableOffers(useUsableOffers().data);
  const removeUpgrade = useCartStore((s) => s.removeUpgrade);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const strategy = strategies?.find((s) => s.id === line.strategyId);
  // Only the live row renews, so it is the one being upgraded even where ended rows share the strategy.
  const subscription = subscriptions.data?.find(
    (s) => s.strategy.id === line.strategyId && s.renewsOn !== null
  );
  const switchDate = subscription?.renewsOn ?? null;
  const notRenewing = subscriptions.data !== undefined && switchDate === null;

  const fitting = offersForLine(offers, line.strategyId, "Yearly");
  const offer = fitting.find((o) => o.id === line.offerId) ?? null;
  const saving = offer ? savingOnLine(offer, line.yearlyPriceUsd) : 0;
  const firstYearUsd = line.yearlyPriceUsd - saving;
  // An offer with no end runs every year; one with an end is spent on the first.
  const offerRunsEveryYear = offer !== null && offer.durationMonths === null;
  const laterYearsUsd = offerRunsEveryYear ? firstYearUsd : line.yearlyPriceUsd;

  const confirmRemove = () => {
    removeUpgrade();
    setConfirmingRemoval(false);
  };

  return (
    <div>
      <h2>Your cart</h2>
      <p className="text-body-secondary mb-3">
        Check what your upgrade costs and when, then proceed to checkout.
      </p>

      <CCard>
        <CCardHeader>Upgrade to yearly</CCardHeader>
        <CCardBody className="p-0">
          {strategy ? (
            <StrategyIdentityBand strategy={strategy} />
          ) : (
            <div className="strategy-band" />
          )}
          <div className="d-flex align-items-center justify-content-between gap-3 p-3">
            <div className="flex-grow-1 min-width-0">
              <Link
                to={`/subscriptions/${line.strategyId}`}
                className="text-reset text-decoration-none"
              >
                <h5 className="mb-0">{line.name}</h5>
              </Link>
              {strategy && (
                <StrategySideLabel direction={strategy.direction} className="small fw-semibold mt-1" />
              )}
              {strategy && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  <StrategyMetaPills strategy={strategy} withRiskProfile withTimeframe={false} />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-3 flex-shrink-0">
              <span className="fs-5 fw-bold">
                {saving > 0 && (
                  <span className="text-body-secondary fw-normal fs-6 text-decoration-line-through me-2">
                    {formatPriceUsd(line.yearlyPriceUsd)}
                  </span>
                )}
                {formatPriceUsd(firstYearUsd)}
                <span className="text-body-secondary fw-normal small"> /yr</span>
              </span>
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                onClick={() => setConfirmingRemoval(true)}
                aria-label={`Remove the upgrade of ${line.name} from cart`}
              >
                <CIcon icon={cilTrash} />
              </CButton>
            </div>
          </div>

          <div className="px-3 pb-3">
            <UpgradeLineOffer line={line} offers={offers} />

            {notRenewing && (
              <CAlert color="warning" className="mt-3 mb-0">
                {line.name} isn't renewing any more, so it can't move to yearly.
                Remove it from your cart.
              </CAlert>
            )}

            {switchDate && (
              <>
                <p className="mt-3 mb-0">
                  Your month is paid to <strong>{formatDateShort(switchDate)}</strong>,
                  and your year starts then.
                </p>
                <div className="border rounded mt-3 px-3">
                  <div className="d-flex justify-content-between gap-3 py-2 border-bottom fw-bold">
                    <span>Due today</span>
                    <span className="text-nowrap">{formatPriceUsd(0)}</span>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span>
                      {formatDateShort(switchDate)}
                      <span className="d-block text-body-secondary small">
                        A year, to {formatDateShort(aYearOn(switchDate))}
                      </span>
                    </span>
                    <span className="text-nowrap">{formatPriceUsd(firstYearUsd)}</span>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-2">
                    <span>Every {formatDayMonth(switchDate)} after</span>
                    <span className="text-nowrap">{formatPriceUsd(laterYearsUsd)}</span>
                  </div>
                </div>
                <p className="text-body-secondary small mt-2 mb-0">
                  {line.name} comes off your monthly bill from{" "}
                  {formatDateShort(switchDate)}.
                </p>
              </>
            )}
          </div>
        </CCardBody>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <Link
          to={routePaths.strategies}
          className="btn btn-outline-secondary"
        >
          Continue browsing
        </Link>
        <CButton
          color="primary"
          disabled={notRenewing}
          onClick={() => navigate(routePaths.checkout)}
        >
          Checkout
        </CButton>
      </div>

      <CModal
        visible={confirmingRemoval}
        onClose={() => setConfirmingRemoval(false)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>Remove from cart?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            Remove the upgrade of <strong>{line.name}</strong> from your cart? It
            stays monthly, and you can upgrade any time from its subscription page.
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="outline"
            onClick={() => setConfirmingRemoval(false)}
          >
            Cancel
          </CButton>
          <CButton color="danger" onClick={confirmRemove}>
            Remove
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}
